import { Input, Checkbox, Popover, Collapse, Tooltip, message } from "antd";
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { stringify } from "svgson";
import { useSelector } from "react-redux";
import { HexAlphaColorPicker } from "react-colorful";
import WatermarkUpload from "./WatermarkUpload";
import JSZip from "jszip";
import { downloadBlob, saveToPhotos, isNative } from "../../../utils/nativeDownload";

const SVGtoPNGResizer = forwardRef(function SVGtoPNGResizer({
  svgJson,
  exportName,
  maintainAspectRatio,
  setMaintainAspectRatio,
}, ref) {
  const currentFile = useSelector((state) => state.files.currentFile);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [aspectRatio, setAspectRatio] = useState(null);
  const [fillColor, setFillColor] = useState("#FFFFFF00");
  const [logo, setLogo] = useState(null);
  const [exportLayersSeparately, setExportLayersSeparately] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const defaultWidth = 1500;

  const updateAspectRatio = (svgJson) => {
    const svgWidth = svgJson.attributes.viewBox.split(" ")[2];
    const svgHeight = svgJson.attributes.viewBox.split(" ")[3];
    const newAspectRatio = svgHeight / svgWidth;
    setAspectRatio(newAspectRatio);
    setWidth(defaultWidth);
    setHeight(Math.round(defaultWidth * newAspectRatio));
  };

  useEffect(() => {
    if (svgJson) {
      updateAspectRatio(svgJson);
    } else {
      setWidth(defaultWidth);
      setHeight(
        aspectRatio ? Math.round(defaultWidth * aspectRatio) : defaultWidth
      );
    }
  }, [svgJson, defaultWidth]);

  useEffect(() => {
    if (maintainAspectRatio && aspectRatio) {
      setHeight(Math.round(width * aspectRatio));
    }
  }, [width, maintainAspectRatio, aspectRatio]);

  const handleWidthChange = (e) => {
    const newWidth = parseInt(e.target.value, 10);
    setWidth(newWidth);
    if (maintainAspectRatio && aspectRatio) {
      setHeight(Math.round(newWidth * aspectRatio));
    }
  };

  const handleHeightChange = (e) => {
    const newHeight = parseInt(e.target.value, 10);
    setHeight(newHeight);
    if (maintainAspectRatio && aspectRatio) {
      setWidth(Math.round(newHeight / aspectRatio));
    }
  };

  const renderSvgToPng = (svgData, targetWidth, targetHeight) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const img = new Image();
      img.src = "data:image/svg+xml;base64," + btoa(svgData);

      img.onload = function () {
        const ctx = canvas.getContext("2d");

        if (!maintainAspectRatio) {
          ctx.fillStyle = fillColor;
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          let drawWidth = targetWidth;
          let drawHeight = targetHeight;
          let offsetX = 0;
          let offsetY = 0;

          if (targetWidth / targetHeight > img.width / img.height) {
            drawWidth = targetHeight * (img.width / img.height);
            offsetX = (targetWidth - drawWidth) / 2;
          } else {
            drawHeight = targetWidth * (img.height / img.width);
            offsetY = (targetHeight - drawHeight) / 2;
          }

          ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        } else {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        }

        if (logo) {
          const logoSize = Math.max(canvas.width, canvas.height) * 0.1;
          const xStep = logoSize * 2.5;
          const yStep = logoSize * 2.5;
          const rotation = -Math.PI / 6;
          const alpha = 0.4;

          const drawLogo = (x, y) => {
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(rotation);
            ctx.globalAlpha = alpha;
            ctx.drawImage(logo, -logoSize / 2, -logoSize / 2, logoSize, logoSize);
            ctx.restore();
          };

          for (let y = yStep / 2; y < canvas.height; y += yStep) {
            for (let x = xStep / 2; x < canvas.width; x += xStep) {
              drawLogo(x, y);
            }
          }
        }

        canvas.toBlob((blob) => resolve(blob), "image/png");
      };
    });
  };

  const handleExportPng = async () => {
    if (!svgJson) return;
    setIsExporting(true);

    try {
      if (exportLayersSeparately && svgJson.children && svgJson.children.length > 0) {
        const zip = new JSZip();
        const viewBox = svgJson.attributes.viewBox;

        for (let i = 0; i < svgJson.children.length; i++) {
          const layer = svgJson.children[i];
          const layerSvg = {
            name: "svg",
            type: "element",
            attributes: {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: viewBox,
              width: svgJson.attributes.width || viewBox.split(" ")[2],
              height: svgJson.attributes.height || viewBox.split(" ")[3],
            },
            children: [layer],
          };

          const svgString = await stringify(layerSvg);
          const blob = await renderSvgToPng(svgString, width, height);
          
          let layerName = `layer_${i + 1}`;
          const layerFill = layer.attributes?.fill;
          if (layerFill && layerFill.startsWith("#")) {
            layerName = `layer_${i + 1}_${layerFill.replace("#", "")}`;
          }
          zip.file(`${exportName || "export"}_${layerName}.png`, blob);
        }

        const zipBlob = await zip.generateAsync({ type: "blob" });
        await downloadBlob(zipBlob, `${exportName || "export"}_layers.zip`);
      } else {
        const svgToSave = await stringify(svgJson);
        const blob = await renderSvgToPng(svgToSave, width, height);
        await downloadBlob(blob, `${exportName || "File Name"}.png`);
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveToPhotos = async () => {
    if (!svgJson) return;
    setIsExporting(true);
    try {
      const svgToSave = await stringify(svgJson);
      const blob = await renderSvgToPng(svgToSave, width, height);
      const success = await saveToPhotos(blob, `${exportName || "File Name"}.png`);
      if (success) {
        message.success("Saved to Photos!");
      } else {
        message.error("Could not save to Photos. Try downloading instead.");
      }
    } catch (error) {
      console.error("Save to photos failed:", error);
      message.error("Failed to save to Photos.");
    } finally {
      setIsExporting(false);
    }
  };

  useImperativeHandle(ref, () => ({
    export: handleExportPng,
    saveToPhotos: handleSaveToPhotos,
    isExporting,
    isNativePlatform: isNative(),
  }));

  return (
    <div>
      {/* Dimensions */}
      <div style={{
        background: "#f8f9fa",
        borderRadius: "10px",
        padding: "14px",
        border: "1px solid #e9ecef",
        marginBottom: "12px",
      }}>
        <label style={{
          display: "block",
          fontSize: "12px",
          fontWeight: 600,
          color: "#888",
          marginBottom: "10px",
          fontFamily: "DM Sans, sans-serif",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}>
          Dimensions
        </label>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "11px", color: "#999", display: "block", marginBottom: "4px" }}>
              Width (px)
            </label>
            <Input
              value={width}
              type="number"
              onChange={handleWidthChange}
              min={10}
              style={{ borderRadius: "8px" }}
            />
          </div>
          <Tooltip title={maintainAspectRatio ? "Unlock aspect ratio" : "Lock aspect ratio"}>
            <button
              onClick={() => setMaintainAspectRatio(!maintainAspectRatio)}
              style={{
                marginTop: "16px",
                padding: "6px 8px",
                border: "1px solid #e9ecef",
                borderRadius: "6px",
                background: maintainAspectRatio ? "#f8f0ff" : "#fff",
                cursor: "pointer",
                fontSize: "14px",
                lineHeight: 1,
                color: maintainAspectRatio ? "#F453C4" : "#bbb",
                transition: "all 0.15s",
                flexShrink: 0,
              }}
            >
              🔗
            </button>
          </Tooltip>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "11px", color: "#999", display: "block", marginBottom: "4px" }}>
              Height (px)
            </label>
            <Input
              value={height}
              type="number"
              onChange={handleHeightChange}
              min={10}
              disabled={maintainAspectRatio}
              style={{ borderRadius: "8px" }}
            />
          </div>
        </div>
      </div>

      {/* Options */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        marginBottom: "12px",
      }}>
        <label style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "13px",
          color: "#555",
          cursor: "pointer",
          padding: "6px 0",
        }}>
          <Checkbox
            checked={exportLayersSeparately}
            onChange={(e) => setExportLayersSeparately(e.target.checked)}
          />
          Export each layer as separate file (ZIP)
        </label>
      </div>

      {/* Watermark - collapsed by default */}
      <Collapse
        style={{ 
          marginBottom: "12px",
          background: "#f8f9fa",
          borderRadius: "10px",
          border: "1px solid #e9ecef",
          overflow: "hidden",
        }}
        expandIconPosition="end"
      >
        <Collapse.Panel 
          key="watermark" 
          header={
            <span style={{ fontWeight: 500, fontSize: "13px", color: "#555" }}>
              {logo ? "✓ Watermark Added" : "Add Watermark"}
            </span>
          }
        >
          <WatermarkUpload
            logo={logo}
            onLogoUpload={setLogo}
            onLogoRemove={() => setLogo(null)}
          />
        </Collapse.Panel>
      </Collapse>

      {/* Fill Color - shown when aspect ratio unlocked */}
      {!maintainAspectRatio && (
        <div style={{
          background: "#f8f9fa",
          borderRadius: "10px",
          padding: "14px",
          border: "1px solid #e9ecef",
          marginBottom: "12px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}>
          <Popover
            trigger="click"
            content={
              <HexAlphaColorPicker color={fillColor} onChange={setFillColor} />
            }
            placement="right"
          >
            <div
              style={{
                width: "36px",
                height: "36px",
                border: "1px solid #d9d9d9",
                borderRadius: "8px",
                position: "relative",
                cursor: "pointer",
                flexShrink: 0,
                backgroundImage:
                  "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)",
                backgroundSize: "8px 8px",
                backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: fillColor,
                  borderRadius: "7px",
                }}
              />
            </div>
          </Popover>
          <div>
            <span style={{ fontSize: "13px", fontWeight: 500, color: "#555" }}>Fill Color</span>
            <span style={{ fontSize: "11px", color: "#aaa", display: "block" }}>
              Background padding color
            </span>
          </div>
        </div>
      )}
    </div>
  );
});

export default SVGtoPNGResizer;
